<script setup>
import { ref, computed, onMounted, watch } from 'vue';
import { useAlert } from 'dashboard/composables';
import BankSettingsAPI, { BankApiKeysAPI } from 'dashboard/api/bankSettings';

// Task 5 (credenciais por caixa): esta tela deixou de ser por CONTA e virou
// uma aba DENTRO da caixa (Settings.vue > aba "Credenciais dos Bancos").
// O inboxId vem do componente pai (BankCredentialsPage.vue) e viaja em toda
// chamada — o backend escopa e autoriza por ele (admin OU membro da inbox).
const props = defineProps({
  inboxId: {
    type: [Number, String],
    required: true,
  },
});

const loading = ref(false);
const saving = ref(false);
const banks = ref([]);
// Correção A (2026-08-12): catálogo canônico dos bancos suportados, servido
// pelo backend junto com `data` (credenciais configuradas desta caixa). A
// tela sempre mostra o catálogo inteiro — o cliente não "cria" banco, só
// preenche as chaves dele nos bancos que quiser (ver BANCOS_CATALOGO no
// bank_settings_controller).
const catalogo = ref([]);
const keys = ref([]);
const newKey = ref('');

const showModal = ref(false);
const isEdit = ref(false);
const hasPassword = ref(false);
const hasApiToken = ref(false);
const form = ref({});

const origin = window.location.origin;

const authExample = [
  '{',
  '  "method": "POST",',
  '  "url": "https://auth.banco.com/token",',
  '  "headers": { "Content-Type": "application/x-www-form-urlencoded" },',
  '  "body_format": "form",',
  '  "body": { "grant_type": "password", "username": "{{username}}", "password": "{{password}}" },',
  '  "token_path": "access_token",',
  '  "expires_in_path": "expires_in"',
  '}',
].join('\n');

// v43: modelo de login explicito — 'basic' (so o token, ex. UY3/FACTA) ou
// 'senha' (usuario+senha, ex. C6/SOMA/V8). No modo basic o JSON e montado
// sozinho; o avancado continua existindo para casos fora do padrao.
const emptyForm = () => ({
  id: null,
  bank_name: '',
  auth_url: '',
  login_tipo: 'basic',
  scope: '',
  username: '',
  password: '',
  api_token: '',
  authText: '',
  refresh_interval_minutes: 0,
  codigo_origem_6: '',
  // NOVO SAQUE: ordem de preferência das tabelas comerciais (10 posições).
  // Vazio = "Escolher prioridade" naquela posição.
  prioridade_tabelas: ['', '', '', '', '', '', '', '', '', ''],
  // Remarketing: 2ª oferta para quem não respondeu (tabela e tempo por caixa)
  remarketing_on: false,
  remarketing_minutos: 10,
  remarketing_tabela: '',
  // Recuperação: quem viu a oferta e sumiu recebe outra tabela depois.
  recuperacao_ativa: false,
  recuperacao_minutos: 30,
  recuperacao_tabela: '',
  numero_cpf_certificado: '',
  desativado: false,
  use_proxy: false,
  proxy_url: '',
});

const fetchBanks = async () => {
  loading.value = true;
  try {
    const { data } = await BankSettingsAPI.get(props.inboxId);
    banks.value = data.data || [];
    catalogo.value = data.catalogo || [];
  } catch (e) {
    useAlert('Erro ao carregar os bancos');
  } finally {
    loading.value = false;
  }
};

const fetchKeys = async () => {
  try {
    const { data } = await BankApiKeysAPI.get(props.inboxId);
    keys.value = data.data || [];
  } catch (e) {
    keys.value = [];
  }
};

// Merge catálogo × credenciais desta caixa: TODO banco do catálogo aparece
// sempre; se já tem credencial configurada, a linha carrega os dados dela
// (id, token, etc.) — senão fica "sem credencial" com Editar pra o cliente
// preencher as chaves dele.
const catalogRows = computed(() =>
  catalogo.value.map(cat => {
    const configured = banks.value.find(b => b.bank_name === cat.id);
    return configured
      ? { ...configured, catalogId: cat.id, catalogNome: cat.nome, configured: true }
      : { catalogId: cat.id, catalogNome: cat.nome, configured: false };
  })
);

const tokenStatus = bank => {
  // Banco desativado: não gera token e não aparece no simulador.
  if (bank.enabled === false)
    return { label: 'DESATIVADO', cls: 'text-slate-400 font-semibold uppercase' };
  if (!bank.has_token) return { label: 'Sem token', cls: 'text-slate-400' };
  const exp = bank.token_expires_at ? new Date(bank.token_expires_at) : null;
  if (exp && exp < new Date())
    return { label: 'Expirado', cls: 'text-amber-600' };
  return { label: 'Ativo', cls: 'text-green-600' };
};

// Correção A: não existe mais "criar banco do zero" — o Editar de uma linha
// "sem credencial" do catálogo abre o MESMO form de cadastro, já com o
// bank_name daquele banco pré-selecionado (e travado: o catálogo é fixo).
const bankNameLocked = ref(false);

// Task G (2026-08-12): o catálogo (`catalogo`) agora carrega o PRESET
// técnico de cada banco (auth_url/auth_config/refresh_interval_minutes +
// `campos` — o que o cliente preenche, com o label certo). `currentPreset`
// é o preset do banco selecionado no form; `isPresetAdd` é true só quando
// estamos ADICIONANDO (não editando) um banco do catálogo — é quando
// escondemos a parte técnica e só mostramos os campos do cliente.
// Tabelas do banco selecionado (só o NOVO SAQUE tem, hoje).
const tabelasDoBanco = computed(() => currentPreset.value?.tabelas || []);

// Uma tabela só pode ocupar UMA prioridade: as já escolhidas somem das outras
// listas (menos da própria posição, senão o select apareceria vazio ao reabrir).
const tabelasDisponiveis = pos => {
  const usadas = form.value.prioridade_tabelas.filter(
    (t, i) => t && i !== pos
  );
  return tabelasDoBanco.value.filter(t => !usadas.includes(t));
};

const currentPreset = computed(() =>
  catalogo.value.find(c => c.id === form.value.bank_name)
);
const isPresetAdd = computed(
  () => !isEdit.value && bankNameLocked.value && !!currentPreset.value
);
// Controla se o bloco "Configuração avançada" abre sozinho. Ao adicionar via
// preset ele nasce FECHADO (já preenchido corretamente, não precisa olhar);
// ao editar, mantém o comportamento antigo (abre se o JSON salvo foge do
// padrão simples "Basic + client_credentials" que o modo simples gera).
const advancedOpen = ref(false);

// Label do campo pro cliente, com o texto do preset (fallback pro genérico
// se o banco não estiver no catálogo ou não tiver esse campo mapeado).
const fieldLabel = (key, fallback) => {
  const found = (currentPreset.value?.campos || []).find(c => c.key === key);
  return found ? found.label : fallback;
};

// Aplica o preset técnico do banco no form: URL/JSON de autenticação/
// intervalo de refresh — SEMPRE o JSON exato do preset (mais confiável que
// tentar reconstruir com o gerador "modo simples", que só cobre o padrão
// Basic+client_credentials). O login_tipo é deduzido dos `campos`: se o
// cliente preenche `api_token`, é o modo "Token (Basic)"; senão, usuário e
// senha.
const applyPreset = preset => {
  if (!preset) return;
  form.value.auth_url = preset.auth_url || '';
  form.value.refresh_interval_minutes = preset.refresh_interval_minutes || 0;
  form.value.authText = preset.auth_config
    ? JSON.stringify(preset.auth_config, null, 2)
    : '';
  const camposKeys = (preset.campos || []).map(c => c.key);
  form.value.login_tipo = camposKeys.includes('api_token') ? 'basic' : 'senha';
};

// Botão "Restaurar padrão do banco" (bloco Avançado): reaplica o preset
// técnico sem tocar no que o cliente já digitou (usuário/senha/token).
const restorePreset = () => {
  applyPreset(currentPreset.value);
};

const openAddForBank = bankId => {
  isEdit.value = false;
  hasPassword.value = false;
  hasApiToken.value = false;
  bankNameLocked.value = true;
  form.value = { ...emptyForm(), bank_name: bankId };
  applyPreset(catalogo.value.find(c => c.id === bankId));
  advancedOpen.value = false;
  showModal.value = true;
};

const openEdit = async bank => {
  isEdit.value = true;
  bankNameLocked.value = false;
  showModal.value = true;
  form.value = emptyForm();
  try {
    const { data } = await BankSettingsAPI.show(bank.id, props.inboxId);
    hasPassword.value = data.has_password;
    hasApiToken.value = data.has_api_token;
    const cfg = data.auth_config || {};
    const cfgStr = JSON.stringify(cfg);
    // URL sai de dentro do JSON e vive no campo do topo (pedido do Kairo).
    const authUrl = data.auth_url || cfg.url || '';
    // Config "padrao Basic" (o que o modo simples gera) vira campos limpos —
    // sem JSON na tela. Qualquer coisa fora disso cai no Avancado.
    const ehBasicPadrao =
      cfg.headers?.Authorization === 'Basic {{token}}' &&
      (cfg.body_format || 'json') === 'form' &&
      cfg.body?.grant_type === 'client_credentials' &&
      Object.keys(cfg.body || {}).every(k => ['grant_type', 'scope'].includes(k));
    // Mesmo comportamento de sempre: abre o Avançado sozinho só quando o
    // JSON salvo foge do padrão simples (o "Restaurar padrão do banco"
    // continua disponível ali dentro pra voltar ao preset).
    advancedOpen.value = !ehBasicPadrao && Object.keys(cfg).length > 0;
    form.value = {
      id: data.id,
      bank_name: data.bank_name,
      auth_url: authUrl,
      login_tipo:
        data.has_api_token || cfgStr.includes('{{token}}') ? 'basic' : 'senha',
      scope: ehBasicPadrao ? cfg.body?.scope || '' : '',
      username: data.username || '',
      password: '',
      api_token: '',
      authText:
        !ehBasicPadrao && Object.keys(cfg).length
          ? JSON.stringify(
              Object.fromEntries(Object.entries(cfg).filter(([k]) => k !== 'url')),
              null,
              2
            )
          : '',
      refresh_interval_minutes: data.refresh_interval_minutes || 0,
      codigo_origem_6: data.codigo_origem_6 || '',
      prioridade_tabelas: [
        ...(data.prioridade_tabelas || []),
        ...Array(10).fill(''),
      ].slice(0, 10),
      remarketing_on: !!data.remarketing_on,
      remarketing_minutos: Number(data.remarketing_minutos) || 10,
      remarketing_tabela: data.remarketing_tabela || '',
      recuperacao_ativa: (data.recuperacao || {}).ativa === true,
      recuperacao_minutos: (data.recuperacao || {}).minutos || 30,
      recuperacao_tabela: (data.recuperacao || {}).tabela || '',
      numero_cpf_certificado: data.numero_cpf_certificado || '',
      desativado: data.enabled === false,
      use_proxy: data.use_proxy === true,
      proxy_url: data.proxy_url || '',
    };
  } catch (e) {
    useAlert('Erro ao abrir o banco');
    showModal.value = false;
  }
};

const closeModal = () => {
  showModal.value = false;
};

const save = async () => {
  if (!isEdit.value && !form.value.bank_name.trim()) {
    useAlert('Informe o nome do banco');
    return;
  }
  let authConfig = {};
  const t = (form.value.authText || '').trim();
  if (t) {
    try {
      authConfig = JSON.parse(t);
    } catch (e) {
      useAlert('O "JSON de Autenticação" não é um JSON válido');
      return;
    }
  } else if (form.value.login_tipo === 'basic' && form.value.auth_url.trim()) {
    // Modo simples: monta o OAuth2 client_credentials sozinho. A URL vem do
    // campo do topo (auth_url) — nunca de dentro do JSON.
    // Exige a URL: banco de token estatico (NOVO SAQUE) nao tem endereco de
    // login, e montar um OAuth2 apontando pra lugar nenhum so deixaria lixo
    // salvo na credencial.
    const body = { grant_type: 'client_credentials' };
    if (form.value.scope.trim()) body.scope = form.value.scope.trim();
    authConfig = {
      method: 'POST',
      headers: {
        Authorization: 'Basic {{token}}',
        'Content-Type': 'application/x-www-form-urlencoded',
      },
      body_format: 'form',
      body,
      token_path: 'access_token',
      expires_in_path: 'expires_in',
    };
  }
  // Aceita o token colado com o prefixo ("Basic MjhhMGFi…"): o header ja poe
  // o "Basic ", entao o prefixo duplicaria e o banco negaria o login.
  const tokenLimpo = form.value.api_token.trim().replace(/^basic\s+/i, '');
  saving.value = true;
  const payload = {
    bank_setting: {
      bank_name: form.value.bank_name.trim(),
      auth_url: form.value.auth_url,
      username: form.value.login_tipo === 'basic' ? '' : form.value.username,
      password: form.value.login_tipo === 'basic' ? '' : form.value.password,
      api_token: tokenLimpo,
      auth_config: authConfig,
      refresh_interval_minutes: Number(form.value.refresh_interval_minutes) || 0,
      enabled: !form.value.desativado,
      codigo_origem_6: form.value.codigo_origem_6,
      // manda só as posições preenchidas: o backend guarda a ordem e ignora
      // buraco/duplicata
      prioridade_tabelas: form.value.prioridade_tabelas.filter(t => t),
      remarketing_on: form.value.remarketing_on,
      remarketing_minutos: Number(form.value.remarketing_minutos) || 10,
      remarketing_tabela: form.value.remarketing_tabela,
      // objeto próprio: some inteiro do payload quando desligada, para não
      // deixar config morta no cofre
      recuperacao: form.value.recuperacao_ativa
        ? {
            ativa: true,
            minutos: Number(form.value.recuperacao_minutos) || 30,
            tabela: form.value.recuperacao_tabela || '',
          }
        : { ativa: false },
      numero_cpf_certificado: form.value.numero_cpf_certificado,
      use_proxy: form.value.use_proxy,
      proxy_url: form.value.use_proxy ? form.value.proxy_url : '',
    },
  };
  try {
    if (isEdit.value) {
      await BankSettingsAPI.update(form.value.id, props.inboxId, payload);
    } else {
      await BankSettingsAPI.create(props.inboxId, payload);
    }
    useAlert('Banco salvo com sucesso!');
    showModal.value = false;
    await fetchBanks();
  } catch (e) {
    useAlert(e?.response?.data?.errors?.[0] || 'Erro ao salvar o banco');
  } finally {
    saving.value = false;
  }
};

const generateNow = async bank => {
  useAlert(`Gerando token do ${bank.bank_name.toUpperCase()}…`);
  try {
    await BankSettingsAPI.refreshToken(bank.id, props.inboxId);
    useAlert('Token gerado com sucesso!');
    await fetchBanks();
  } catch (e) {
    useAlert(e?.response?.data?.errors?.[0] || 'Falha ao gerar o token');
    await fetchBanks();
  }
};

const removeBank = async bank => {
  // eslint-disable-next-line no-alert
  if (!window.confirm(`Remover o banco "${bank.bank_name}"?`)) return;
  try {
    await BankSettingsAPI.delete(bank.id, props.inboxId);
    useAlert('Banco removido');
    await fetchBanks();
  } catch (e) {
    useAlert('Erro ao remover');
  }
};

const createKey = async () => {
  // eslint-disable-next-line no-alert
  const label = window.prompt('Nome do sistema que vai usar esta chave:', 'sistema');
  if (label === null) return;
  try {
    const { data } = await BankApiKeysAPI.create(props.inboxId, { label });
    newKey.value = data.api_key;
    await fetchKeys();
  } catch (e) {
    useAlert('Erro ao gerar a chave');
  }
};

const revokeKey = async key => {
  // eslint-disable-next-line no-alert
  if (!window.confirm(`Revogar a chave "${key.label}"? Os sistemas que a usam param de funcionar.`)) return;
  try {
    await BankApiKeysAPI.delete(key.id, props.inboxId);
    await fetchKeys();
  } catch (e) {
    useAlert('Erro ao revogar');
  }
};

const copyKey = async () => {
  try {
    await navigator.clipboard.writeText(newKey.value);
    useAlert('Chave copiada!');
  } catch (e) {
    useAlert('Copie manualmente a chave acima.');
  }
};

// Só vazio se o CATÁLOGO vier vazio (não deve acontecer — os bancos são
// fixos). Uma caixa nova sem nenhuma credencial ainda mostra o catálogo
// inteiro, cada linha "sem credencial" + Editar.
const isEmpty = computed(() => !loading.value && catalogo.value.length === 0);

onMounted(() => {
  fetchBanks();
  fetchKeys();
});

// Defesa extra caso este componente algum dia seja reaproveitado sem
// remontar ao trocar de caixa (hoje o Settings.vue remonta a aba por v-if).
watch(
  () => props.inboxId,
  () => fetchBanks()
);
</script>

<template>
  <div class="p-6 mx-auto max-w-4xl">
    <!-- Cabeçalho -->
    <div class="mb-6">
      <h1 class="text-2xl font-semibold text-slate-900 dark:text-slate-50">
        Credenciais dos Bancos
      </h1>
      <p class="mt-1 text-sm text-slate-500 dark:text-slate-400">
        As integrações com os bancos abaixo já existem, prontas — você só
        coloca as chaves da SUA caixa em cada uma. O token é gerado
        automaticamente no intervalo definido e servido aos sistemas via chave
        de API — sem expor as credenciais. Estas credenciais são desta caixa:
        só quem é admin ou membro dela pode ver e editar.
      </p>
    </div>

    <div v-if="loading" class="py-12 text-center text-slate-400">Carregando…</div>

    <!-- Só acontece se o catálogo vier vazio do backend — não é um estado de
         negócio (o catálogo é fixo, sempre tem bancos), é falha técnica
         (chamada quebrou ou devolveu algo inesperado). Mensagem diferenciada
         pra não confundir o cliente achando que precisa "cadastrar" algo.
         Tokens n-ruby-* (não `dark:red-*` do Tailwind padrão, que este
         Chatwoot não gera — ver CLAUDE.md "Gotchas"): já usados no mesmo
         padrão de erro em SimuladorModal.vue, tema-conscientes sem `dark:`. -->
    <div
      v-else-if="isEmpty"
      class="rounded-xl border border-dashed border-n-ruby-7 py-14 text-center"
    >
      <p class="text-n-ruby-11">
        Não consegui carregar o catálogo de bancos. Recarregue a página.
      </p>
    </div>

    <!-- Lista: catálogo canônico inteiro, sempre — cada linha traz o status
         DESTA caixa (configurado ou "sem credencial" + Editar). -->
    <!-- overflow-x-auto, NUNCA overflow-hidden: a coluna "Ações" é a última, então
         quando a tabela passa da largura do painel ela era a primeira a ser cortada
         -- e o corte é SILENCIOSO, sem barra de rolagem. O admin via a linha do banco
         sem nenhum botão e concluía que não tinha permissão para editar. Acontecia só
         em ALGUMAS caixas porque a largura depende do tamanho do usuário salvo. -->
    <div v-else class="overflow-x-auto rounded-xl border border-slate-200 dark:border-slate-700">
      <table class="w-full text-sm">
        <thead class="bg-slate-50 dark:bg-slate-800 text-slate-500 dark:text-slate-400">
          <tr>
            <th class="px-4 py-3 text-left font-medium">Banco</th>
            <th class="px-4 py-3 text-left font-medium">Usuário</th>
            <th class="px-4 py-3 text-left font-medium">Renova</th>
            <th class="px-4 py-3 text-left font-medium">Token</th>
            <th class="px-4 py-3 text-right font-medium">Ações</th>
          </tr>
        </thead>
        <tbody class="divide-y divide-slate-100 dark:divide-slate-800">
          <tr
            v-for="row in catalogRows"
            :key="row.catalogId"
            class="bg-white dark:bg-slate-900 hover:bg-slate-50 dark:hover:bg-slate-800"
          >
            <td class="px-4 py-3 font-semibold uppercase text-slate-800 dark:text-slate-100">
              {{ row.catalogNome }}
            </td>

            <template v-if="row.configured">
              <td class="px-4 py-3 text-slate-600 dark:text-slate-300">
                <!-- e-mail/chave de banco é longo (isadora.marques@rdcpromotora.com.br)
                     e não quebra: era ele que empurrava a tabela para fora do painel.
                     Truncado com o valor inteiro no title. -->
                <span
                  class="inline-block max-w-[220px] truncate align-bottom"
                  :title="row.username || ''"
                >{{ row.username || '—' }}</span>
                <span v-if="row.has_password" class="ml-1 text-xs text-slate-400" title="Senha salva">🔒</span>
              </td>
              <td class="px-4 py-3 text-slate-600 dark:text-slate-300">
                {{ row.refresh_interval_minutes > 0 ? row.refresh_interval_minutes + ' min' : '—' }}
              </td>
              <td class="px-4 py-3">
                <span :class="['font-medium', tokenStatus(row).cls]">{{ tokenStatus(row).label }}</span>
                <!-- Banco desativado: só "DESATIVADO", sem erro antigo de refresh. -->
                <div
                  v-if="row.enabled !== false && row.last_refresh_status && row.last_refresh_status !== 'ok'"
                  class="text-xs text-red-500 max-w-[180px] truncate"
                  :title="row.last_refresh_status"
                >
                  {{ row.last_refresh_status }}
                </div>
              </td>
              <td class="px-4 py-3">
                <div class="flex justify-end gap-3 whitespace-nowrap">
                  <button type="button" class="text-green-600 hover:underline" @click="generateNow(row)">
                    Gerar Token
                  </button>
                  <button type="button" class="text-blue-600 hover:underline" @click="openEdit(row)">
                    Editar
                  </button>
                  <button type="button" class="text-red-600 hover:underline" @click="removeBank(row)">
                    Remover
                  </button>
                </div>
              </td>
            </template>

            <template v-else>
              <td class="px-4 py-3 italic text-slate-400">sem credencial</td>
              <td class="px-4 py-3 text-slate-400">—</td>
              <td class="px-4 py-3 text-slate-400">Sem token</td>
              <td class="px-4 py-3">
                <div class="flex justify-end gap-3 whitespace-nowrap">
                  <button
                    type="button"
                    class="text-blue-600 hover:underline"
                    @click="openAddForBank(row.catalogId)"
                  >
                    Editar
                  </button>
                </div>
              </td>
            </template>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- Chaves de API -->
    <div class="mt-8 rounded-xl border border-slate-200 dark:border-slate-700 p-5">
      <div class="flex items-start justify-between">
        <div>
          <h2 class="text-lg font-semibold text-slate-900 dark:text-slate-50">
            Chaves de acesso (API)
          </h2>
          <p class="mt-1 text-sm text-slate-500 dark:text-slate-400">
            Os sistemas usam a chave pra ler o token — sem ver usuário/senha:
          </p>
          <code class="mt-1 inline-block rounded bg-slate-100 dark:bg-slate-800 px-2 py-1 text-xs">
            GET {{ origin }}/bank-token/&lt;banco&gt; · header <b>X-Api-Key</b>
          </code>
        </div>
        <button
          type="button"
          class="rounded-lg border border-green-600 px-3 py-2 text-sm font-medium text-green-700 hover:bg-green-50 dark:hover:bg-green-950"
          @click="createKey"
        >
          Gerar chave
        </button>
      </div>

      <!-- chave recém-criada (aparece só uma vez) -->
      <div
        v-if="newKey"
        class="mt-4 rounded-lg border border-green-300 bg-green-50 dark:bg-green-950/40 p-3"
      >
        <p class="text-xs text-green-700 dark:text-green-300 mb-1">
          Copie agora — esta chave não será exibida de novo:
        </p>
        <div class="flex items-center gap-2">
          <code class="flex-1 break-all text-sm">{{ newKey }}</code>
          <button type="button" class="text-sm text-green-700 hover:underline" @click="copyKey">Copiar</button>
          <button type="button" class="text-sm text-slate-400 hover:underline" @click="newKey = ''">Fechar</button>
        </div>
      </div>

      <table v-if="keys.length" class="w-full mt-4 text-sm">
        <thead class="text-slate-500 dark:text-slate-400">
          <tr>
            <th class="py-2 text-left font-medium">Sistema</th>
            <th class="py-2 text-left font-medium">Chave</th>
            <th class="py-2 text-left font-medium">Status</th>
            <th class="py-2 text-right font-medium">Ações</th>
          </tr>
        </thead>
        <tbody class="divide-y divide-slate-100 dark:divide-slate-800">
          <tr v-for="k in keys" :key="k.id">
            <td class="py-2 text-slate-700 dark:text-slate-200">{{ k.label }}</td>
            <td class="py-2 font-mono text-xs text-slate-500">{{ k.masked }}</td>
            <td class="py-2">
              <span :class="k.revoked ? 'text-red-500' : 'text-green-600'">
                {{ k.revoked ? 'Revogada' : 'Ativa' }}
              </span>
            </td>
            <td class="py-2 text-right">
              <button
                v-if="!k.revoked"
                type="button"
                class="text-red-600 hover:underline"
                @click="revokeKey(k)"
              >
                Revogar
              </button>
            </td>
          </tr>
        </tbody>
      </table>
      <p v-else class="mt-3 text-sm text-slate-400">Nenhuma chave gerada ainda.</p>
    </div>

    <!-- Modal adicionar/editar -->
    <div
      v-if="showModal"
      class="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4 overflow-y-auto"
      @click.self="closeModal"
    >
      <div class="w-full max-w-lg rounded-xl bg-white dark:bg-slate-900 p-6 shadow-xl my-8">
        <h2 class="mb-4 text-xl font-semibold text-slate-900 dark:text-slate-50">
          {{ isEdit ? 'Editar banco' : 'Adicionar banco' }}
        </h2>

        <div class="space-y-4">
          <label class="block">
            <span class="text-sm font-medium text-slate-700 dark:text-slate-300">Nome do banco</span>
            <input
              v-model="form.bank_name"
              type="text"
              :disabled="isEdit || bankNameLocked"
              placeholder="ex.: uy3, facta, bmg…"
              class="mt-1 block w-full rounded-lg border border-slate-300 dark:border-slate-600 dark:bg-slate-800 px-3 py-2 disabled:opacity-60"
            >
          </label>

          <!-- Task G: banco do catálogo em modo ADICIONAR já vem com a parte
               técnica pronta (preset) — avisa o cliente que ele só preenche
               o que é dele, sem precisar saber URL/JSON nenhum. -->
          <p
            v-if="isPresetAdd"
            class="-mt-2 text-sm text-slate-500 dark:text-slate-400"
          >
            A integração deste banco já está pronta. Preencha só os dados
            abaixo — a configuração técnica (URL, autenticação, renovação)
            já vem preenchida com o padrão do banco em "Configuração
            avançada".
          </p>

          <!-- Modelo de login: Token Basic (um campo so) ou usuario+senha.
               Escondido ao adicionar um banco do catálogo — o preset já
               define o modo certo pra aquele banco. -->
          <div v-if="!isPresetAdd" class="block">
            <span class="text-sm font-medium text-slate-700 dark:text-slate-300">Login com</span>
            <div class="mt-1 flex gap-2">
              <button
                type="button"
                class="rounded-lg border px-3 py-1.5 text-sm transition-colors"
                :class="form.login_tipo === 'basic'
                  ? 'border-woot-500 bg-woot-500 text-white'
                  : 'border-slate-300 dark:border-slate-600 text-slate-600 dark:text-slate-300'"
                @click="form.login_tipo = 'basic'"
              >
                Token (Basic)
              </button>
              <button
                type="button"
                class="rounded-lg border px-3 py-1.5 text-sm transition-colors"
                :class="form.login_tipo === 'senha'
                  ? 'border-woot-500 bg-woot-500 text-white'
                  : 'border-slate-300 dark:border-slate-600 text-slate-600 dark:text-slate-300'"
                @click="form.login_tipo = 'senha'"
              >
                Usuário e senha
              </button>
            </div>
          </div>

          <template v-if="form.login_tipo === 'basic'">
            <label class="block">
              <span class="text-sm font-medium text-slate-700 dark:text-slate-300">
                {{ fieldLabel('api_token', 'Token Basic') }}
              </span>
              <span class="block text-xs text-slate-400 mb-1">
                Cole o token do banco — pode vir com o prefixo "Basic ", eu ajusto ao salvar.
              </span>
              <input
                v-model="form.api_token"
                type="password"
                autocomplete="new-password"
                :placeholder="isEdit && hasApiToken ? '•••••••• (manter)' : 'Basic MjhhMGFi…'"
                class="mt-1 block w-full rounded-lg border border-slate-300 dark:border-slate-600 dark:bg-slate-800 px-3 py-2 font-mono text-xs"
              >
            </label>
            <label v-if="!isPresetAdd" class="block">
              <span class="text-sm font-medium text-slate-700 dark:text-slate-300">Scope (opcional)</span>
              <span class="block text-xs text-slate-400 mb-1">
                Só se o banco exigir. Ex.: credit/SegueFinanceiraAPI (UY3).
              </span>
              <input
                v-model="form.scope"
                type="text"
                autocomplete="off"
                class="mt-1 block w-full rounded-lg border border-slate-300 dark:border-slate-600 dark:bg-slate-800 px-3 py-2 font-mono text-xs"
              >
            </label>
          </template>

          <div v-else class="grid grid-cols-2 gap-3">
            <label class="block">
              <span class="text-sm font-medium text-slate-700 dark:text-slate-300">
                {{ fieldLabel('username', 'Usuário') }}
              </span>
              <input
                v-model="form.username"
                type="text"
                autocomplete="off"
                class="mt-1 block w-full rounded-lg border border-slate-300 dark:border-slate-600 dark:bg-slate-800 px-3 py-2"
              >
            </label>
            <label class="block">
              <span class="text-sm font-medium text-slate-700 dark:text-slate-300">
                {{ fieldLabel('password', 'Senha') }}
              </span>
              <input
                v-model="form.password"
                type="password"
                autocomplete="new-password"
                :placeholder="isEdit && hasPassword ? '•••••••• (manter)' : ''"
                class="mt-1 block w-full rounded-lg border border-slate-300 dark:border-slate-600 dark:bg-slate-800 px-3 py-2"
              >
            </label>
          </div>

          <!-- Credenciais extras do C6 (só aparecem ao editar o banco C6).
               Salvas no campo `extra` (jsonb) e usadas na digitação de proposta. -->
          <template v-if="form.bank_name === 'c6'">
            <label class="block">
              <span class="text-sm font-medium text-slate-700 dark:text-slate-300">
                {{ fieldLabel('codigo_origem_6', 'Código de Origem (C6)') }}
              </span>
              <span class="block text-xs text-slate-400 mb-1">codigo_origem_6 — parceiro/originador da conta C6.</span>
              <input
                v-model="form.codigo_origem_6"
                type="text"
                class="mt-1 block w-full rounded-lg border border-slate-300 dark:border-slate-600 dark:bg-slate-800 px-3 py-2"
              >
            </label>

            <label class="block">
              <span class="text-sm font-medium text-slate-700 dark:text-slate-300">
                {{ fieldLabel('numero_cpf_certificado', 'CPF do Certificado (C6)') }}
              </span>
              <span class="block text-xs text-slate-400 mb-1">numero_cpf_certificado — CPF do certificado digital da conta C6.</span>
              <input
                v-model="form.numero_cpf_certificado"
                type="text"
                class="mt-1 block w-full rounded-lg border border-slate-300 dark:border-slate-600 dark:bg-slate-800 px-3 py-2"
              >
            </label>
          </template>

          <!-- NOVO SAQUE: ordem de preferência das tabelas comerciais.
               A simulação volta com VÁRIAS tabelas (ZERO, SMART, MONEY…), todas
               com o mesmo saldo devedor e valores liberados diferentes. Qual
               oferecer primeiro é decisão comercial da caixa — o sistema tenta
               a 1ª e, se ela não vier na resposta daquele CPF, cai pra 2ª, e
               assim por diante. -->
          <template v-if="tabelasDoBanco.length">
            <div class="rounded-lg border border-slate-200 dark:border-slate-700 px-3 py-3">
              <span class="text-sm font-medium text-slate-700 dark:text-slate-300">
                Prioridade das tabelas
              </span>
              <span class="block text-xs text-slate-400 mb-2">
                A oferta usa a 1ª tabela da lista que existir para o CPF. Sem
                nenhuma escolhida, vale a de MAIOR valor liberado.
              </span>
              <div class="grid grid-cols-2 gap-2">
                <label
                  v-for="(_, i) in form.prioridade_tabelas"
                  :key="`prio-${i}`"
                  class="flex items-center gap-2"
                >
                  <span class="text-xs text-slate-500 w-5 text-right">{{ i + 1 }}</span>
                  <select
                    v-model="form.prioridade_tabelas[i]"
                    class="block w-full rounded-lg border border-slate-300 dark:border-slate-600 dark:bg-slate-800 px-2 py-1.5 text-sm"
                  >
                    <option value="">Escolher prioridade</option>
                    <option v-for="t in tabelasDisponiveis(i)" :key="t" :value="t">
                      {{ t }}
                    </option>
                  </select>
                </label>
              </div>
            </div>
          </template>

          <!-- RECUPERAÇÃO (pedido do Kairo, 18/08): o cliente viu a oferta e
               sumiu sem fechar. Passado o tempo configurado, o sistema volta a
               falar com ele oferecendo OUTRA tabela ("falei com meu gerente e
               consegui uma condição melhor"), com um botão para contratar na
               hora. Fica aqui, junto da prioridade, porque é decisão comercial
               da caixa — e o liga/desliga é explícito. -->
          <template v-if="tabelasDoBanco.length">
            <div class="rounded-lg border border-slate-200 dark:border-slate-700 px-3 py-3">
              <label class="flex items-center gap-2">
                <input v-model="form.recuperacao_ativa" type="checkbox" />
                <span class="text-sm font-medium text-slate-700 dark:text-slate-300">
                  Recuperação — oferecer outra tabela para quem não fechou
                </span>
              </label>
              <span class="block text-xs text-slate-400 mt-1">
                Desligada, nada é enviado. Ligada, quem viu a oferta e não
                respondeu recebe uma nova condição depois do tempo abaixo.
              </span>

              <div v-if="form.recuperacao_ativa" class="mt-3 grid grid-cols-2 gap-3">
                <label class="block">
                  <span class="text-sm font-medium text-slate-700 dark:text-slate-300">
                    Esperar (minutos)
                  </span>
                  <input
                    v-model.number="form.recuperacao_minutos"
                    type="number" min="1" max="1440"
                    class="mt-1 w-full rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 px-3 py-2 text-sm"
                    placeholder="30"
                  />
                  <span class="block text-xs text-slate-400 mt-1">De 1 a 1440 (24h).</span>
                </label>

                <label class="block">
                  <span class="text-sm font-medium text-slate-700 dark:text-slate-300">
                    Tabela da nova oferta
                  </span>
                  <select
                    v-model="form.recuperacao_tabela"
                    class="mt-1 w-full rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 px-3 py-2 text-sm"
                  >
                    <option value="">A melhor disponível</option>
                    <option v-for="t in tabelasDoBanco" :key="`rec-${t}`" :value="t">{{ t }}</option>
                  </select>
                  <span class="block text-xs text-slate-400 mt-1">
                    Precisa ser diferente da que já foi oferecida.
                  </span>
                </label>
              </div>
            </div>
          </template>

          <!-- REMARKETING: 2ª oferta para quem recebeu a 1ª e não respondeu.
               Tempo e tabela são decisão comercial da caixa. -->
          <template v-if="tabelasDoBanco.length">
            <div class="rounded-lg border border-slate-200 dark:border-slate-700 px-3 py-3">
              <label class="flex items-center gap-2 cursor-pointer">
                <input v-model="form.remarketing_on" type="checkbox" style="margin:0" />
                <span class="text-sm font-medium text-slate-700 dark:text-slate-300">
                  Enviar 2ª oferta se o cliente não responder
                </span>
              </label>
              <span class="block text-xs text-slate-400 mt-1 mb-2">
                O cliente recebe a oferta e fica em silêncio: passado o tempo abaixo,
                mandamos outra oferta com a tabela escolhida aqui. Uma vez por atendimento.
              </span>
              <div v-if="form.remarketing_on" class="flex gap-3">
                <label class="flex-1">
                  <span class="block text-xs text-slate-500 mb-1">Esperar (minutos)</span>
                  <input
                    v-model.number="form.remarketing_minutos"
                    type="number" min="1" max="1440"
                    class="block w-full rounded-lg border border-slate-300 dark:border-slate-600 dark:bg-slate-800 px-2 py-1.5 text-sm"
                  />
                </label>
                <label class="flex-1">
                  <span class="block text-xs text-slate-500 mb-1">Tabela da 2ª oferta</span>
                  <select
                    v-model="form.remarketing_tabela"
                    class="block w-full rounded-lg border border-slate-300 dark:border-slate-600 dark:bg-slate-800 px-2 py-1.5 text-sm"
                  >
                    <option value="">A 2ª da prioridade</option>
                    <option v-for="t in tabelasDoBanco" :key="`rmk-${t}`" :value="t">{{ t }}</option>
                  </select>
                </label>
              </div>
            </div>
          </template>

          <!-- Task G: campos TÉCNICOS (URL, JSON de autenticação, intervalo
               de refresh) colapsados aqui — pré-preenchidos pelo preset do
               banco ao adicionar, editáveis pra casos raros de ajuste. Ao
               editar um banco já configurado, os valores são os salvos
               (nunca sobrescritos pelo preset sem o clique explícito em
               "Restaurar padrão"). -->
          <details class="rounded-lg border border-slate-200 dark:border-slate-700 px-3 py-2" :open="advancedOpen">
            <summary class="cursor-pointer text-sm font-medium text-slate-500 dark:text-slate-400 select-none">
              Configuração avançada
              <span v-if="isPresetAdd">(já preenchida com o padrão do banco)</span>
            </summary>

            <div class="mt-3 space-y-3">
              <button
                v-if="currentPreset"
                type="button"
                class="text-xs font-medium text-woot-500 hover:underline"
                @click="restorePreset"
              >
                Restaurar padrão do banco
              </button>

              <label class="block">
                <span class="text-sm font-medium text-slate-700 dark:text-slate-300">
                  URL de autenticação
                </span>
                <input
                  v-model="form.auth_url"
                  type="text"
                  autocomplete="off"
                  placeholder="https://auth.banco.com/oauth2/token"
                  class="mt-1 block w-full rounded-lg border border-slate-300 dark:border-slate-600 dark:bg-slate-800 px-3 py-2 font-mono text-xs"
                >
              </label>

              <label class="block">
                <span class="text-sm font-medium text-slate-700 dark:text-slate-300">
                  Renovar o token a cada (minutos)
                </span>
                <input
                  v-model.number="form.refresh_interval_minutes"
                  type="number"
                  min="0"
                  placeholder="0 = manual"
                  class="mt-1 block w-40 rounded-lg border border-slate-300 dark:border-slate-600 dark:bg-slate-800 px-3 py-2"
                >
                <span class="block text-xs text-slate-400 mt-1">0 = só gera manualmente (botão "Gerar agora").</span>
              </label>

              <label class="block">
                <span class="text-sm font-medium text-slate-700 dark:text-slate-300">
                  JSON de autenticação
                </span>
                <span class="block text-xs text-slate-400 mt-2 mb-1">
                  A URL fica SEMPRE no campo acima, não aqui. Use
                  <code v-pre>{{username}}</code>, <code v-pre>{{password}}</code> e <code v-pre>{{token}}</code> pra referenciar as credenciais salvas.
                </span>
                <textarea
                  v-model="form.authText"
                  rows="8"
                  spellcheck="false"
                  :placeholder="authExample"
                  class="mt-1 block w-full rounded-lg border border-slate-300 dark:border-slate-600 dark:bg-slate-800 px-3 py-2 font-mono text-xs"
                />
              </label>
            </div>
          </details>

          <!-- Desativado: se marcado, para de renovar o token no intervalo e o
               banco sai do uso (enabled=false). -->
          <label class="flex items-center gap-2 cursor-pointer">
            <input
              v-model="form.desativado"
              type="checkbox"
              class="rounded border-slate-300 dark:border-slate-600"
            >
            <span class="text-sm font-medium text-slate-700 dark:text-slate-300">
              Desativado (não gera token e não é usado)
            </span>
          </label>

          <!-- Proxy opcional por banco: Sim/Não + caixa pra colar o proxy. -->
          <label class="flex items-center gap-2 cursor-pointer">
            <input
              v-model="form.use_proxy"
              type="checkbox"
              class="rounded border-slate-300 dark:border-slate-600"
            >
            <span class="text-sm font-medium text-slate-700 dark:text-slate-300">
              Usar Proxy
            </span>
          </label>
          <label v-if="form.use_proxy" class="block">
            <span class="text-sm font-medium text-slate-700 dark:text-slate-300">Proxy</span>
            <span class="block text-xs text-slate-400 mb-1">Formato: http://usuario:senha@host:porta/</span>
            <input
              v-model="form.proxy_url"
              type="text"
              spellcheck="false"
              placeholder="http://usuario:senha@host:porta/"
              class="mt-1 block w-full rounded-lg border border-slate-300 dark:border-slate-600 dark:bg-slate-800 px-3 py-2 font-mono text-xs"
            >
          </label>
        </div>

        <div class="mt-6 flex gap-2">
          <button
            type="button"
            class="flex-1 rounded-lg bg-green-600 py-2 font-medium text-white hover:bg-green-700 disabled:opacity-60"
            :disabled="saving"
            @click="save"
          >
            {{ saving ? 'Salvando…' : 'Salvar' }}
          </button>
          <button
            type="button"
            class="flex-1 rounded-lg border border-slate-300 dark:border-slate-600 py-2 hover:bg-slate-50 dark:hover:bg-slate-800"
            @click="closeModal"
          >
            Cancelar
          </button>
        </div>
      </div>
    </div>
  </div>
</template>
